import { fabric } from 'fabric';
import {
  ensureArrowHead,
  syncArrowHeadWithLine,
} from '../../../../components/CanvasEditor/helpers/fabricHelpers';

const scaleStrokeWidth = (obj, ratio) => {
  if (!obj || ratio === 1) return;

  const applyScale = (target) => {
    if (!target) return;

    if (typeof target.strokeWidth === 'number') {
      target.set({
        strokeUniform: true,
        strokeWidth: Math.max(0.1, target.strokeWidth * ratio),
      });
      target.setCoords?.();
    }

    if (typeof target.getObjects === 'function') {
      target.getObjects().forEach(applyScale);
    }
  };

  applyScale(obj);
  obj.setCoords?.();
};

const scaleSerializedStrokeWidth = (obj, ratio) => {
  if (!obj || ratio === 1) return obj;

  const next = { ...obj };

  if (typeof next.strokeWidth === 'number') {
    next.strokeWidth = Math.max(0.1, next.strokeWidth * ratio);
  }

  if (Array.isArray(next.objects)) {
    next.objects = next.objects.map((child) =>
      scaleSerializedStrokeWidth(child, ratio)
    );
  }

  return next;
};

const scaleSerializedToCanvas = (annotation, canvasWidth, canvasHeight) => {
  if (!annotation) return annotation;

  const sourceBaseWidth = Number(annotation.pdfBaseWidth);
  const sourceBaseHeight = Number(annotation.pdfBaseHeight);

  if (
    !Number.isFinite(sourceBaseWidth) ||
    !Number.isFinite(sourceBaseHeight) ||
    sourceBaseWidth <= 0 ||
    sourceBaseHeight <= 0
  ) {
    // Legacy annotations without explicit PDF base dimensions are kept as-is.
    return annotation;
  }

  const ratioX = canvasWidth / sourceBaseWidth;
  const ratioY = canvasHeight / sourceBaseHeight;
  const strokeRatio = (ratioX + ratioY) / 2;

  const scaled = {
    ...annotation,
    left: annotation.left * ratioX,
    top: annotation.top * ratioY,
    scaleX: annotation.scaleX * ratioX,
    scaleY: annotation.scaleY * ratioY,
  };

  if (annotation.objectType === 'line' || annotation.objectType === 'arrow') {
    scaled.x1 = annotation.x1 * ratioX;
    scaled.y1 = annotation.y1 * ratioY;
    scaled.x2 = annotation.x2 * ratioX;
    scaled.y2 = annotation.y2 * ratioY;
  }

  return scaleSerializedStrokeWidth(scaled, strokeRatio);
};

/**
 * Restores annotations received from backend JSON
 * into a Fabric canvas.
 *
 * If serialized annotations include `pdfBaseWidth` / `pdfBaseHeight`,
 * coordinates are scaled to current canvas dimensions.
 * Legacy annotations without those fields are restored as-is.
 */
export function restoreBackendAnnotations(
  canvas,
  annotations,
  readOnly = false,
) {
  if (!canvas || !annotations?.length) return;

  canvas.clear();

  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();
  const scaledAnnotations = annotations.map((annotation) =>
    scaleSerializedToCanvas(annotation, canvasWidth, canvasHeight)
  );

  fabric.util.enlivenObjects(scaledAnnotations, (objects) => {
    objects.forEach((obj) => {
      if (obj.objectType === 'line' || obj.objectType === 'arrow') {
        const dx = Math.abs(obj.x1);
        const dy = Math.abs(obj.y1);
        const { left, top } = obj;

        obj.x1 += left + dx;
        obj.x2 += left + dx;
        obj.y1 += top + dy;
        obj.y2 += top + dy;

        obj.left = Math.min(obj.x1, obj.x2);
        obj.top = Math.min(obj.y1, obj.y2);
        obj.scaleX = 1;
        obj.scaleY = 1;
        obj.startLeft = obj.left;
        obj.startTop = obj.top;

        obj.setCoords();
      }

      if (readOnly) {
        obj.set({
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
        });
      }

      canvas.add(obj);

      if (obj.objectType === 'arrow') {
        ensureArrowHead(obj);
      }
    });

    canvas.requestRenderAll();
  });
}

/**
 * Restores annotations from one Fabric canvas to another
 * and rescales them based on canvas size change.
 *
 * Used when switching pages, zoom levels, or resolutions.
 */
export function restoreAnnotationsScaled({
  sourceCanvas,
  targetCanvas,
  oldWidth,
  oldHeight,
  newWidth,
  newHeight,
  readOnly = false,
}) {
  if (!sourceCanvas || !targetCanvas) return;

  const ratioX = newWidth / oldWidth;
  const ratioY = newHeight / oldHeight;
  const strokeRatio = (ratioX + ratioY) / 2;

  // // In-place resize when source and target are the same canvas.
  // if (sourceCanvas === targetCanvas) {
  //   sourceCanvas.getObjects().forEach((obj) => {
  //     obj.set({
  //       left: obj.left * ratioX,
  //       top: obj.top * ratioY,
  //       scaleX: obj.scaleX * ratioX,
  //       scaleY: obj.scaleY * ratioY,
  //     });
  //   });

  //   sourceCanvas.requestRenderAll();
  //   return;
  // }

  const objects = sourceCanvas
    .getObjects()
    .filter((obj) => obj.objectType !== 'arrow-head')
    .map((obj) => ({
      obj,
      left: obj.left,
      top: obj.top,
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      x1: obj.x1,
      y1: obj.y1,
      x2: obj.x2,
      y2: obj.y2,
    }));

  targetCanvas.clear();

  objects.forEach(({ obj, left, top, scaleX, scaleY, x1, y1, x2, y2 }) => {
    if (obj.objectType === 'line' || obj.objectType === 'arrow') {
      const nextX1 = x1 * ratioX;
      const nextY1 = y1 * ratioY;
      const nextX2 = x2 * ratioX;
      const nextY2 = y2 * ratioY;

      obj.set({
        // left: nextLeft,
        // top: nextTop,
        x1: nextX1,
        y1: nextY1,
        x2: nextX2,
        y2: nextY2,
      });
    } else {
      obj.set({
        left: left * ratioX,
        top: top * ratioY,
        scaleX: scaleX * ratioX,
        scaleY: scaleY * ratioY,
      });
    }

    scaleStrokeWidth(obj, strokeRatio);

    if (obj.objectType === 'line' || obj.objectType === 'arrow') {
      obj.startLeft = obj.left;
      obj.startTop = obj.top;
    }

    if (readOnly) {
      obj.set({
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
      });
    }

    obj.setCoords?.();
    sourceCanvas.remove(obj);
    targetCanvas.add(obj);

    if (obj.objectType === 'arrow') {
      ensureArrowHead(obj);
      syncArrowHeadWithLine(obj, { updateSize: true, bringToFront: true });
    }
  });
}

/**
 * Ensures that a Fabric object stays within canvas bounds.
 *
 * Prevents dragging or scaling outside the visible canvas area.
 */
export function clampObjectToCanvas(obj, canvas) {
  if (!obj || !canvas) return;

  obj.setCoords();

  const bounds = obj.getBoundingRect(true);
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  let dx = 0;
  let dy = 0;

  if (bounds.left < 0) dx = -bounds.left;
  if (bounds.top < 0) dy = -bounds.top;
  if (bounds.left + bounds.width > canvasWidth)
    dx = canvasWidth - (bounds.left + bounds.width);
  if (bounds.top + bounds.height > canvasHeight)
    dy = canvasHeight - (bounds.top + bounds.height);

  obj.left += dx;
  obj.top += dy;

  obj.setCoords();
}

/**
 * Normalizes Fabric annotations for backend storage.
 *
 * Converts current canvas coordinates to base (original) dimensions
 * and persists those base dimensions (`pdfBaseWidth` / `pdfBaseHeight`)
 * so annotations can be restored consistently across devices.
 */
export function normalizeAnnotationsForBackend(
  canvas,
  canvasWidth,
  canvasHeight,
  baseWidth,
  baseHeight
) {
  const ratioX = baseWidth / canvasWidth;
  const ratioY = baseHeight / canvasHeight;
  const strokeRatio = (ratioX + ratioY) / 2;

  return canvas
    .getObjects()
    .filter(
      (obj) =>
        !['controlpoint', 'arrow-head'].includes(obj.objectType)
    )
    .map((obj) => {
      const base = obj.toObject([
        'objectType',
        'originalLeft',
        'originalTop',
        'userFullName',
        'userID',
        'pageNumber',
        'objectCaching',
      ]);

      return scaleSerializedStrokeWidth(
        {
          ...base,
          pdfBaseWidth: baseWidth,
          pdfBaseHeight: baseHeight,
          left: obj.left * ratioX,
          top: obj.top * ratioY,
          scaleX: obj.scaleX * ratioX,
          scaleY: obj.scaleY * ratioY,
          angle: obj.angle || 0,
        },
        strokeRatio
      );
    });
}
